import React, { useCallback, useEffect, useState } from 'react'
import { Grid, LinearProgress } from '@mui/material'
import { useFormContext, UseFormSetValue, useFormState } from 'react-hook-form'
import { v4 as uuidv4 } from 'uuid'
import useAuth from '../../../../hooks/useAuth'
import IssueApi from '../../../../api/IssueApi'
import IssuesGroupApi from '../../../../api/IssuesGroupApi'
import ConvertiblesApi from '../../../../api/ConvertiblesApi'
import { AllCompanyIssues } from './AllCompanyIssues'
import { IssueEntity } from 'types/IssueEntity'
import { ConvertibleEntity } from 'types/ConvertibleEntity'
import { IssuesGroup } from 'types/IssuesGroupEntity'
import { IssueGroups } from './IssueGroups'
import { FormIssueEntity } from '../../types/FormIssueEntity'
import { FormConvertibleEntity } from '../../types/FormConvertibleEntity'

const useSetFormGroups = (
    setValue: UseFormSetValue<FormIssueEntity | FormConvertibleEntity>,
    entity: FormIssueEntity | FormConvertibleEntity,
) => {
    const setLinkedIssues = useCallback(
        (groups: IssuesGroup[]) => {
            setValue(
                'linkedIssues',
                groups.map((g) => {
                    if (!entity.id) return g

                    if (isEntityIssue(entity)) {
                        return {
                            ...g,
                            issues: g.issues.filter((i) => i.id !== entity.id),
                        }
                    } else {
                        return {
                            ...g,
                            convertibles: g.convertibles.filter((c) => c.id !== entity.id),
                        }
                    }

                    return g
                }),
            )
        },
        [entity, setValue],
    )

    return { setLinkedIssues }
}

const isEntityIssue = (entity: FormIssueEntity | FormConvertibleEntity): entity is FormIssueEntity => 'type' in entity

const issueApi = new IssueApi()
const convertibleApi = new ConvertiblesApi()
const issuesGroupApi = new IssuesGroupApi()

function LinkedIssuesDetails({
    setIsCustomFormDirty,
}: {
    setIsCustomFormDirty: (v: boolean) => void
} & (
    | {
          entityType: 'issue'
          entity: FormIssueEntity
      }
    | {
          entityType: 'convertible'
          entity: ConvertibleEntity
      }
)) {
    const { isAuthenticated } = useAuth()
    const { setValue, watch } = useFormContext<FormIssueEntity | FormConvertibleEntity>()
    const { isSubmitted } = useFormState()
    const entity = watch()
    const entityType = 'type' in entity ? 'issue' : 'convertible'
    const { setLinkedIssues } = useSetFormGroups(setValue, entity)

    const [issues, setIssues] = useState<IssueEntity[] | null>(null)
    const [convertibles, setConvertibles] = useState<ConvertibleEntity[] | null>(null)
    const [issueGroups, setIssueGroups] = useState<IssuesGroup[] | null>(null)
    const fetchIssueGroups = useCallback(async () => {
        if (!isAuthenticated || !entity.companyId) return

        if (!issueGroups || isSubmitted) {
            // Refetch data when form has been submitted
            const ig = await issuesGroupApi.findByCompanyId(entity.companyId)
            setIssueGroups(ig)
        }
    }, [entity.companyId, isAuthenticated, isSubmitted, issueGroups])

    useEffect(() => {
        async function fetchData() {
            if (!isAuthenticated || !entity.companyId) return
            let fetchedIssues = await issueApi.findByCompanyId(entity.companyId, false)
            fetchedIssues = fetchedIssues.filter((i) => i.id !== entity.id)
            setIssues(fetchedIssues)
        }
        void fetchData()
    }, [isAuthenticated, entity.companyId, entity.id])

    useEffect(() => {
        async function fetchData() {
            if (!isAuthenticated || !entity.companyId) return
            let fetchedConvertibles = await convertibleApi.findByCompanyId(entity.companyId)
            fetchedConvertibles = fetchedConvertibles.filter((c) => c.id !== entity.id)
            setConvertibles(fetchedConvertibles)
        }
        void fetchData()
    }, [isAuthenticated, entity.companyId, entity.id])

    useEffect(() => {
        void fetchIssueGroups()
    }, [fetchIssueGroups])

    if (!issues || !issueGroups) return <LinearProgress />

    const handleLinkToNewGroup = (entities: Array<ConvertibleEntity | IssueEntity>) => {
        const groupId = uuidv4()
        const linkedEntities = [...entities].filter((e) => e !== null)
        const _issues = linkedEntities.filter((e) => e.id && 'type' in e) as IssueEntity[]
        const _convertibles = linkedEntities.filter((e) => e.id && !('type' in e)) as ConvertibleEntity[]

        const newLinks = [
            ...entity.linkedIssues,
            {
                groupId,
                convertibles: _convertibles,
                issues: _issues,
            },
        ]
        setLinkedIssues(newLinks)
        setIsCustomFormDirty(true)
    }
    const handleLinkToGroup = (groupId: string) => {
        const newLinks = entity.linkedIssues.filter((ig) => ig.groupId !== groupId)
        const group = issueGroups.find((ig) => ig.groupId === groupId)

        if (!group) {
            console.error('handleAddToGroup: Failed to find group')

            return
        }

        if (!entity.id) {
            console.error('Cant add an issue without id')
            return
        }

        if (isEntityIssue(entity)) {
            const g = {
                ...group,
                issues: [...group.issues, { ...entity, id: entity.id, issueId: entity.id, convertibleId: null }],
            }
            newLinks.push(g)
        } else {
            const g = { ...entity, convertibleId: entity.id, issueId: null } as IssuesGroup['convertibles'][number]

            newLinks.push({
                ...group,
                convertibles: [...group.convertibles, g],
            })
        }

        setLinkedIssues(newLinks)
        setIsCustomFormDirty(true)
    }
    const handleDeleteLink = (groupId: string) => {
        const newLinks = entity.linkedIssues.filter((ig) => ig.groupId !== groupId)

        setLinkedIssues(newLinks)
        setIsCustomFormDirty(true)
    }

    const isGuarantorFeeIssue = isEntityIssue(entity) && entity.type === 'Guarantor fee issue'
    const hasReachedMaxIssueGroups = isGuarantorFeeIssue && entity.linkedIssues.length > 0

    return (
        <>
            <Grid container spacing={2}>
                <Grid size={6}>
                    <AllCompanyIssues
                        handleCreateGroup={handleLinkToNewGroup}
                        hasReachedMaxIssueGroups={hasReachedMaxIssueGroups}
                        issues={issues}
                        convertibles={convertibles}
                        isGuarantorFeeIssue={isGuarantorFeeIssue}
                        isIssuePage={entityType === 'issue'}
                    />
                </Grid>
                <Grid size={6}>
                    <IssueGroups
                        issueGroups={entity.linkedIssues}
                        handleDeleteFromGroup={handleDeleteLink}
                        handleAddToGroup={handleLinkToGroup}
                        isGuarantorFeeIssue={isGuarantorFeeIssue}
                        title='Emissionens länkgrupper'
                        isLinkedIssues
                    />
                    <IssueGroups
                        issueGroups={issueGroups}
                        handleDeleteFromGroup={handleDeleteLink}
                        handleAddToGroup={handleLinkToGroup}
                        isGuarantorFeeIssue={isGuarantorFeeIssue}
                        title='Bolagets övriga länkgrupper'
                    />
                </Grid>
            </Grid>
        </>
    )
}

export default LinkedIssuesDetails
